import React, { useEffect, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import { Autocomplete, Grid, IconButton, Stack, TextField } from '@mui/material';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { TaskOutput } from 'src/careflow/ph-core/fhir-client/resources';
import { ErrorFieldState } from './TaskModal';
import { GridSection } from 'src/components/CustomModal';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';

type Props = {
  taskOutput: TaskOutput[] | null;
  updateState: Function;
  errors: ErrorFieldState;
  updateErrorState: Function;
  i18n: i18nFunction;
  configurations: any;
};

const TaskOutputComponent = ({
  taskOutput,
  errors,
  updateState,
  updateErrorState,
  i18n,
  configurations,
}: Props) => {
  const { valueSets: [taskOutputTypeVS], isLoading: isFetchingVS } =
    useValueSetsByIdentifiers(['ph-task-output-types']);

  useEffect(() => {
    if (taskOutput === undefined) {
      updateState({
        taskOutputs: [{ type: {}, valueString: '' }],
      });
    }
  }, []);

  const handleAddOutput = () => {
    updateState({
      taskOutputs: [
        ...(taskOutput ?? []),
        { type: {}, valueString: '' },
      ],
    });
  };

  const outputTypeCodeableConcept = useMemo(
    () =>
      (taskOutputTypeVS?.asList?.() ?? []).map((concept) => ({
        coding: [
          {
            code: concept?.code,
            display: concept?.display,
            system: taskOutputTypeVS?.getConceptSystem?.(),
          },
        ],
      })),
    [taskOutputTypeVS]
  );

  const displayOutputs = taskOutput && Array.isArray(taskOutput) && taskOutput.length > 0 
    ? taskOutput 
    : [{ type: {}, valueString: '' }];

  const getCurrentOutputs = () => {
    return taskOutput && Array.isArray(taskOutput) && taskOutput.length > 0 
      ? [...taskOutput] 
      : [{ type: {}, valueString: '' }];
  };

  const canEditOutput = checkAclValidation({ 
    acls: [
      crsAcls.CRS.PATIENT.TASKS.INPUT_OUTPUT.EDIT,
      crsAcls.CRS.CASE.TASKS.INPUT_OUTPUT.EDIT
    ] 
  });

  const canDeleteOutput = checkAclValidation({ 
    acls: [
      crsAcls.CRS.PATIENT.TASKS.INPUT_OUTPUT.DELETE,
      crsAcls.CRS.CASE.TASKS.INPUT_OUTPUT.DELETE
    ] 
  });

  return (
    <Stack direction="column" mt={2} ml={2} mb={2}>
      {displayOutputs.map((item: any, index) => {
        const outputValue = item?.valueString ?? item?.valueUrl ?? item?.valueReference?.reference ?? '';

        return (
          <GridSection container spacing={2} key={`output-${index}`} mt={index === 0 ? -2 : 0.4}>
            <Grid item xs={4}>
              <Autocomplete
                disabled={isFetchingVS || !canEditOutput}
                value={item.type ?? undefined}
                fullWidth
                onChange={(_, newValue) => {
                  if (!canEditOutput) return;
                  const currentOutputs = getCurrentOutputs();
                  currentOutputs[index] = { ...currentOutputs[index], type: newValue };
                  if (newValue) updateErrorState({ taskOutputs: null });
                  updateState({ taskOutputs: currentOutputs });
                }}
                options={outputTypeCodeableConcept}
                getOptionLabel={(option: any) =>
                  option?.coding?.[0]?.display ??
                  option?.coding?.[0]?.code ??
                  ''
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n('patients.details.tasks.outputType', 'crs')}
                    variant="outlined"
                    error={errors?.taskOutputs?.includes(index)}
                    helperText={
                      errors?.taskOutputs?.includes(index) &&
                      i18n('patients.details.tasks.outputTypeError', 'crs')
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6.6}>
              <TextField
                fullWidth
                disabled={isFetchingVS || !canEditOutput}
                value={outputValue}
                onChange={(e) => {
                  if (!canEditOutput) return;
                  const currentOutputs = getCurrentOutputs();
                  if (!currentOutputs[index]) {
                    currentOutputs[index] = { type: {}, valueString: '' };
                  }
                  currentOutputs[index].valueString = e.target.value;
                  updateState({ taskOutputs: currentOutputs });
                }}
                label={i18n('patients.details.tasks.output', 'crs')}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={1.4}>
              <IconButton
                onClick={handleAddOutput}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingVS || !canEditOutput}
              >
                <Iconify icon="eva:plus-fill" />
              </IconButton>

              <IconButton
                onClick={(e) => {
                  if (!canDeleteOutput) return;
                  e.stopPropagation();
                  e.preventDefault();
                  const currentOutputs = taskOutput && Array.isArray(taskOutput) && taskOutput.length > 0 
                    ? [...taskOutput] 
                    : [];
                  if (currentOutputs.length === 0) return;
                  const updated = currentOutputs.filter((_, i) => i !== index);
                  updateState({ taskOutputs: updated.length > 0 ? updated : null });
                  if (errors?.taskOutputs) {
                    const newErrors = errors.taskOutputs.filter((_, i) => i !== index);
                    updateErrorState({
                      taskOutputs: newErrors.length > 0 ? newErrors : null,
                    });
                  }
                }}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingVS || !canDeleteOutput}
              >
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>
            </Grid>
          </GridSection>
        );
      })}
    </Stack>
  );
};

export default TaskOutputComponent;
